import React, { useEffect, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import {
  Card, CardBody, CardHeader,
  Button, Alert, Row, Col
} from "reactstrap";
import { useAccount } from "../../context/AccountContext";
import PageTitleAlt2 from "../../Layout/AppMain/PageTitleAlt2";
import { STRATEGY_OPTIONS } from "../../constants/strategies";

const RuleTemplatesPage = () => {
  const { selectedAccount } = useAccount();
  const history = useHistory();
  const [strategyType, setStrategyType] = useState("PYRAMID_HEDGING");
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    setSelected(null);
    setError(null);
    setSuccess(null);
    axios.get(`/rules/templates/${strategyType}`)
      .then(res => setTemplates(res.data || []))
      .catch(() => setError("Failed to load templates."));
  }, [strategyType]);

  const handleImport = async () => {
    if (!selected || !selectedAccount) return;
    try {
      setLoading(true);
      setError(null);
      const rules = JSON.parse(selected.template_json);
      await axios.post("/rules/activate-template", {
        username: selectedAccount,
        strategy_type: strategyType,
        rules
      });
      setSuccess(`Template "${selected.template_name}" imported. Redirecting to Rule Builder...`);
      setTimeout(() => history.push("/rules/builder"), 1500);
    } catch {
      setError("Failed to import template.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageTitleAlt2
        heading="Rule Templates"
        subheading="Browse and import pre-built rule templates into your strategy."
        icon="pe-7s-albums icon-gradient bg-sunny-morning"
      />

      <div style={{ padding: "0 25px 25px" }}>
        {error && <Alert color="danger">{error}</Alert>}
        {success && <Alert color="success">{success}</Alert>}

        {/* Filter bar */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20 }}>
          <select
            value={strategyType}
            onChange={e => setStrategyType(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #ccc", fontSize: 13 }}
          >
            {STRATEGY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <Button
            color="primary"
            disabled={!selected || loading}
            onClick={handleImport}
          >
            {loading ? "Importing..." : "Import Selected Template"}
          </Button>

          <Button color="secondary" outline onClick={() => history.push("/rules/builder")}>
            Go to Rule Builder
          </Button>
        </div>

        {/* Template cards */}
        {templates.length === 0 ? (
          <Alert color="info">No templates found for {strategyType}.</Alert>
        ) : (
          <Row>
            {templates.map(t => (
              <Col md={6} lg={4} key={t.id} style={{ marginBottom: 16 }}>
                <Card
                  onClick={() => setSelected(t)}
                  style={{
                    cursor: "pointer",
                    border: selected?.id === t.id ? "2px solid #007bff" : "1px solid #e0e0e0",
                    borderRadius: 10,
                    transition: "border 0.15s"
                  }}
                >
                  <CardHeader style={{ fontWeight: 600 }}>
                    {t.template_name}
                  </CardHeader>
                  <CardBody style={{ fontSize: 13, color: "#555" }}>
                    {t.description || "No description available."}
                    <div style={{ marginTop: 8, color: "#888", fontSize: 12 }}>
                      {(() => {
                        try {
                          const rules = JSON.parse(t.template_json);
                          return `${Array.isArray(rules) ? rules.length : 0} rule(s)`;
                        } catch { return ""; }
                      })()}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </div>
    </>
  );
};

export default RuleTemplatesPage;
